import React, { useEffect, useMemo, useState } from 'react';
import { ProtectedRoute } from '../../../components/ProtectedRoute';
import { AsyncBoundary } from '../../../components/AsyncBoundary';
import { PaymentFilters, PaymentTable, RecordPaymentModal } from './components';
import {
  Payment,
  backendMethodToUi,
  backendStatusToUi,
  uiMethodToBackend,
} from './paymentsTypes';
import { usePaymentsStore } from '../../../store/paymentsStore';
import { Payment as BackendPayment } from '../../../services/paymentsApi';
import { formatPaymentId, formatInvoiceId } from '../../../utils/formatId';
import styles from './AdminPayments.module.css';

function formatCurrency(n: number): string {
  return `$${Math.abs(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function adapt(p: BackendPayment): Payment {
  return {
    id: p.id,
    paymentId: formatPaymentId(p.id),
    customer: p.customer_name || `Customer #${p.customer_id ?? '—'}`,
    invoiceNumber: p.invoice_number || formatInvoiceId(p.invoice_id),
    method: backendMethodToUi(p.payment_method),
    amount: formatCurrency(p.amount),
    date: formatDate(p.payment_date),
    reference: p.reference_number || '—',
    status: backendStatusToUi(p.status),
  };
}

const AdminPayments: React.FC = () => {
  const [search, setSearch] = useState('');
  const [method, setMethod] = useState('All Methods');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [modalOpen, setModalOpen] = useState(false);

  const items = usePaymentsStore((s) => s.items);
  const loading = usePaymentsStore((s) => s.loading);
  const error = usePaymentsStore((s) => s.error);
  const setFilters = usePaymentsStore((s) => s.setFilters);
  const fetchList = usePaymentsStore((s) => s.fetchList);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  useEffect(() => {
    setFilters({
      method: method === 'All Methods' ? undefined : uiMethodToBackend(method),
      fromDate: dateFrom || undefined,
      toDate: dateTo || undefined,
      search: search.trim() || undefined,
    });
    fetchList();
  }, [method, dateFrom, dateTo, search, setFilters, fetchList]);

  const adapted = useMemo<Payment[]>(() => items.map(adapt), [items]);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1 className={styles.title}>Payment</h1>
        <button className={styles.recordBtn} onClick={() => setModalOpen(true)}>
          <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
            <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
          </svg>
          Record Payment
        </button>
      </div>

      <div className={styles.tableCard}>
        <PaymentFilters
          search={search}
          onSearchChange={setSearch}
          method={method}
          onMethodChange={setMethod}
          dateFrom={dateFrom}
          onDateFromChange={setDateFrom}
          dateTo={dateTo}
          onDateToChange={setDateTo}
        />
        <AsyncBoundary
          loading={loading && items.length === 0}
          error={error}
          empty={!loading && adapted.length === 0}
          emptyMessage="No payments to display"
          onRetry={() => fetchList()}
        >
          <PaymentTable items={adapted} />
        </AsyncBoundary>
      </div>

      <RecordPaymentModal open={modalOpen} onClose={() => setModalOpen(false)} />
    </div>
  );
};

export const AdminPaymentsPage: React.FC = () => (
  <ProtectedRoute requiredRoles={['admin']}>
    <AdminPayments />
  </ProtectedRoute>
);

export { AdminPayments };
export default AdminPayments;
